import {
  SuperAdminBulkGeneratedFssaiTag,
  SuperAdminBulkLookups,
  SuperAdminBulkPreviewRow,
  SuperAdminBulkRecipeIngredient,
} from './super-admin-recipe-bulk-upload.models';

export interface BulkIngredientSearchHit {
  id: number;
  /** IngredientRefs.Id when available — required for RecipeIngredients FK. */
  ingredientRefId?: number | null;
  name: string;
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
  fiber: number;
  quantity: number;
  isVeg?: boolean | null;
  mealPreference?: string;
  healthColorCodeId?: number | null;
  healthColorCodeName?: string;
  colorCode?: number | null;
}

export interface BulkServingUnit {
  servingUnitMasterId: number;
  valueInGram: number;
  unit?: string;
  size?: string;
  type?: string;
  displayLabel?: string;
}

export interface BulkIngredientNutrientRow {
  nutrition: string;
  value: number | string;
  unit: string;
}

export interface BulkFssaiCalculateInput {
  stateOfProduct: string;
  calories?: number;
  protein?: number;
  fat?: number;
  fiber?: number;
  carbs?: number;
  nutrientValues?: Record<string, number>;
  nutrientUnits?: Record<string, string>;
}

export interface BulkIngredientEnricherDeps {
  searchIngredients?: (term: string) => Promise<BulkIngredientSearchHit[]>;
  /** Preferred: resolve all unique names in one (or few) API call(s). */
  bulkResolveIngredients?: (names: string[]) => Promise<Map<string, BulkIngredientSearchHit | null>>;
  getServingUnits: (ingredientId: number) => Promise<BulkServingUnit[]>;
  getIngredientDetail?: (ingredientId: number) => Promise<Partial<BulkIngredientSearchHit> | null>;
  /** Per-100g nutrient rows for an ingredient (micronutrients included). */
  getIngredientNutrientRows?: (ingredientId: number) => Promise<BulkIngredientNutrientRow[]>;
  calculateFssaiTags?: (input: BulkFssaiCalculateInput) => Promise<SuperAdminBulkGeneratedFssaiTag[]>;
}

export interface BulkEnrichOptions {
  /**
   * Upload path: unique-name search only, use file grams for macros.
   * Skips serving-units / detail / FSSAI (FSSAI runs when row editor opens).
   */
  fast?: boolean;
  concurrency?: number;
  calculateFssai?: boolean;
  fetchServingUnits?: boolean;
  fetchDetail?: boolean;
}

type CacheEntry = {
  hit: BulkIngredientSearchHit | null;
  units: BulkServingUnit[];
  unitsLoaded?: boolean;
};

/** Shared across one browser session so re-opening rows does not re-search. */
const sharedIngredientCache = new Map<string, CacheEntry>();

function isGramLikeUnit(unitLabel: string): boolean {
  const needle = normalizeUnit(unitLabel);
  return (
    needle === 'g' ||
    needle === 'gm' ||
    needle === 'gram' ||
    needle === 'grams' ||
    needle === 'kg' ||
    needle === 'kilogram' ||
    needle === 'kilograms'
  );
}

/** Rough grams when serving-units API is skipped (bulk fast path) or unit not in master. */
function estimateGramsFromUnit(
  unitLabel: string,
  quantity: number,
  ingredientName = ''
): number {
  if (!(quantity > 0)) return 0;
  const n = normalizeUnit(unitLabel);
  if (!n) return 0;

  const name = String(ingredientName || '').toLowerCase();
  const isLiquid =
    /\b(oil|ghee|water|milk|juice|curd|dahi|butter|achaar|pickle|lemon)\b/i.test(name) ||
    n === 'ml' ||
    n.includes('milli');

  if (n === 'g' || n === 'gm' || n === 'gram' || n === 'grams') return quantity;
  if (n === 'kg' || n === 'kilogram' || n === 'kilograms') return quantity * 1000;
  if (n === 'ml' || n.includes('milli')) return quantity;
  if (n === 'tsp' || n === 'tsps' || n.includes('teaspoon')) return quantity * 5;
  if (n === 'tbsp' || n === 'tbsps' || n.includes('tablespoon')) return quantity * 15;
  if (n === 'cup' || n === 'cups') return quantity * (isLiquid ? 240 : 120);
  if (n === 'pinch' || n === 'pinches') return quantity * 0.3;
  if (n === 'nos' || n === 'no' || n === 'pc' || n === 'pcs' || n.includes('piece')) {
    return quantity * 10;
  }
  if (n.includes('clove')) return quantity * 3;
  return 0;
}

/**
 * Decide total grams for an ingredient line.
 * Never treat cup/tsp ApproximateValue=1 as 1 gram.
 */
function resolveIngredientTotalGrams(
  quantity: number,
  approximateValue: number,
  unitLabel: string,
  ingredientName: string,
  unit: BulkServingUnit | null
): number {
  const qty = Number(quantity) || 0;
  const approx = Number(approximateValue) || 0;
  const label = String(unitLabel || '');

  if (unit && qty > 0 && Number(unit.valueInGram) > 0) {
    return Number(unit.valueInGram) * qty;
  }

  if (isGramLikeUnit(label)) {
    if (qty > 0 && approx > 0 && Math.abs(qty - approx) > 0.0001) {
      return qty * approx;
    }
    return approx > 0 ? approx : qty;
  }

  const estimated = estimateGramsFromUnit(label, qty, ingredientName);
  if (estimated > 0) return estimated;

  // Approx looks like real grams only when clearly larger than a unit count.
  if (approx > 0 && (!qty || approx > qty * 5)) return approx;
  return 0;
}

function stripIngredientNoteNoise(name: string): string {
  return String(name || '')
    .replace(
      /\([^)]*(for dough|for filling|for cooking|finely|grated|chopped|as required|plus salt)[^)]*\)/gi,
      ' '
    )
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeName(value: string): string {
  return stripIngredientNoteNoise(value)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeUnit(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/\s+/g, '');
}

function pickBestSearchHit(
  hits: BulkIngredientSearchHit[],
  name: string
): BulkIngredientSearchHit | null {
  if (!hits?.length) return null;
  const needle = normalizeName(name);
  if (!needle) return null;

  const exact = hits.find((h) => normalizeName(h.name) === needle);
  if (exact) return exact;
  const starts = hits.find(
    (h) =>
      normalizeName(h.name).startsWith(needle) || needle.startsWith(normalizeName(h.name))
  );
  if (starts) return starts;

  const contains = hits.find(
    (h) => normalizeName(h.name).includes(needle) || needle.includes(normalizeName(h.name))
  );
  if (contains) {
    const hitName = normalizeName(contains.name);
    // Short labels like "oil"/"salt" must not bind to long unrelated USDA titles.
    if (needle.length <= 5 && hitName.length > Math.max(18, needle.length * 4)) {
      const tighter = hits.find((h) => {
        const n = normalizeName(h.name);
        return (
          n === needle ||
          n.startsWith(needle + ' ') ||
          n.endsWith(' ' + needle) ||
          n.includes(' ' + needle + ' ')
        );
      });
      return tighter || null;
    }
    return contains;
  }

  if (needle.length <= 5) return null;
  return hits[0] || null;
}

function matchServingUnit(
  units: BulkServingUnit[],
  unitLabel: string,
  quantity: number,
  approximateValue: number
): BulkServingUnit | null {
  if (!units?.length) return null;
  const needle = normalizeUnit(unitLabel);

  const scoreUnit = (u: BulkServingUnit): number => {
    const unit = normalizeUnit(u.unit || '');
    const label = normalizeUnit(u.displayLabel || '');
    const size = normalizeUnit(u.size || '');
    let score = 0;
    if (!needle) return score;
    if (unit === needle || label === needle || size === needle) score += 10;
    if (unit.includes(needle) || label.includes(needle) || size.includes(needle)) score += 5;
    if (
      (needle === 'g' || needle === 'gm' || needle === 'gram' || needle === 'grams') &&
      (unit.includes('gram') || unit === 'g' || unit === 'gm')
    ) {
      score += 8;
      if (Number(u.valueInGram) === 1) score += 4;
    }
    if (
      (needle === 'tsp' || needle === 'teaspoon' || needle.includes('tsp')) &&
      (unit.includes('tea') || unit.includes('tsp') || label.includes('tsp'))
    ) {
      score += 8;
    }
    if (
      (needle === 'tbsp' || needle === 'tablespoon' || needle.includes('tbsp')) &&
      (unit.includes('table') || unit.includes('tbsp') || label.includes('tbsp'))
    ) {
      score += 8;
    }
    if (
      (needle === 'ml' || needle === 'millilitre' || needle === 'milliliter') &&
      (unit.includes('ml') || unit.includes('milli'))
    ) {
      score += 8;
    }
    if (
      (needle === 'cup' || needle === 'cups') &&
      (unit.includes('cup') || label.includes('cup') || size.includes('cup'))
    ) {
      score += 8;
    }
    // Prefer units where qty * valueInGram ≈ approximate grams from file.
    if (quantity > 0 && approximateValue > 0 && Number(u.valueInGram) > 0) {
      const predicted = quantity * Number(u.valueInGram);
      const delta = Math.abs(predicted - approximateValue);
      if (delta <= 0.05) score += 12;
      else if (delta / approximateValue <= 0.1) score += 6;
    }
    return score;
  };

  let best: BulkServingUnit | null = null;
  let bestScore = 0;
  for (const u of units) {
    const score = scoreUnit(u);
    if (score > bestScore) {
      bestScore = score;
      best = u;
    }
  }
  if (best && bestScore > 0) return best;

  // Fallback: 1g unit when label is grams / empty and approx ≈ quantity.
  if (!needle || needle === 'g' || needle === 'gm' || needle === 'gram' || needle === 'grams') {
    const oneGram = units.find((u) => Number(u.valueInGram) === 1);
    if (oneGram) return oneGram;
  }
  return units[0] || null;
}

function inferTotalGrams(
  unit: BulkServingUnit | null,
  quantity: number,
  approximateValue: number,
  unitLabel: string
): number {
  if (unit && quantity > 0 && Number(unit.valueInGram) > 0) {
    return Number(unit.valueInGram) * quantity;
  }
  if (approximateValue > 0) return approximateValue;
  const needle = normalizeUnit(unitLabel);
  if (
    quantity > 0 &&
    (needle === 'g' || needle === 'gm' || needle === 'gram' || needle === 'grams' || !needle)
  ) {
    return quantity;
  }
  return quantity > 0 ? quantity : 0;
}

function scaleMacros(
  hit: BulkIngredientSearchHit,
  totalGrams: number
): Pick<SuperAdminBulkRecipeIngredient, 'calories' | 'protein' | 'fat' | 'carbs' | 'fiber'> {
  const baseQty = hit.quantity > 0 ? hit.quantity : 100;
  const ratio = totalGrams > 0 ? totalGrams / baseQty : 0;
  return {
    calories: (Number(hit.calories) || 0) * ratio,
    protein: (Number(hit.protein) || 0) * ratio,
    fat: (Number(hit.fat) || 0) * ratio,
    carbs: (Number(hit.carbs) || 0) * ratio,
    fiber: (Number(hit.fiber) || 0) * ratio,
  };
}

function dietaryRank(label: string): number {
  const key = String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[-_]+/g, ' ');
  if (!key) return 0;
  if (key.includes('non') && key.includes('veg')) return 4;
  if (key.includes('egg')) return 3;
  if (key.includes('vegan')) return 1;
  if (key.includes('veg')) return 2;
  return 0;
}

function resolveIngredientDietaryLabel(ing: SuperAdminBulkRecipeIngredient): string {
  const raw = String(ing.mealPreference || '').trim();
  if (raw) {
    const lower = raw.toLowerCase();
    if (lower.includes('non') && lower.includes('veg')) return 'Non Veg';
    if (
      (lower.includes('egg') && !lower.includes('eggplant') && !lower.includes('egg plant')) ||
      /\begg\b/.test(lower) ||
      lower.includes('eggetarian')
    ) {
      return 'Egg';
    }
    if (lower.includes('vegan')) return 'Vegan';
    if (lower.includes('veg') || lower.includes('vegetarian')) return 'Veg';
  }
  if (ing.isVeg === false) return 'Non Veg';
  if (ing.isVeg === true) return 'Veg';
  return '';
}

function findDietaryIdByLabel(
  lookups: SuperAdminBulkLookups,
  label: string
): number | null {
  if (!label || !lookups.dietaryPreferences?.length) return null;
  const lower = label.toLowerCase();
  const match = lookups.dietaryPreferences.find((o) => {
    const n = String(o.name || '').toLowerCase();
    if (lower.includes('non') && lower.includes('veg')) {
      return n.includes('non') && n.includes('veg');
    }
    if (lower.includes('vegan')) return n.includes('vegan');
    if (lower.includes('egg')) return n.includes('egg') && !n.includes('eggplant');
    if (lower.includes('veg')) {
      return (n.includes('veg') || n.includes('vegetarian')) && !n.includes('non') && !n.includes('vegan');
    }
    return n === lower;
  });
  const id = Number(match?.id);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function healthRank(lookups: SuperAdminBulkLookups, id: string | null | undefined): number {
  if (!id) return 0;
  const opt = lookups.healthColorCodes.find((c) => String(c.id) === String(id));
  const text = `${opt?.name || ''} ${opt?.label || ''}`.toUpperCase();
  if (text.includes('DEL')) return 4;
  if (text.includes('ALT')) return 3;
  if (text.includes('CTRL') || text.includes('CONTROL')) return 2;
  if (text.includes('EAT')) return 1;
  return 0;
}

function resolveHealthIdFromIngredient(
  lookups: SuperAdminBulkLookups,
  ing: SuperAdminBulkRecipeIngredient
): string | null {
  if (!lookups.healthColorCodes?.length) return null;
  if (ing.healthColorCodeId) {
    const byId = lookups.healthColorCodes.find((c) => String(c.id) === String(ing.healthColorCodeId));
    if (byId) return String(byId.id);
  }
  const legacy = Number(ing.colorCode);
  if (Number.isFinite(legacy) && legacy >= 1 && legacy <= 4) {
    const byLegacy = lookups.healthColorCodes.find((c) => Number(c.id) === legacy);
    if (byLegacy) return String(byLegacy.id);
    const labels = ['', 'EAT', 'CTRL', 'ALT', 'DEL'];
    const label = labels[legacy];
    const byLabel = lookups.healthColorCodes.find((c) =>
      `${c.name || ''} ${c.label || ''}`.toUpperCase().includes(label)
    );
    if (byLabel) return String(byLabel.id);
  }
  const name = String(ing.healthColorCodeName || '').trim().toUpperCase();
  if (name) {
    const byName = lookups.healthColorCodes.find((c) => {
      const text = `${c.name || ''} ${c.label || ''}`.toUpperCase();
      return text.includes(name) || name.includes(text);
    });
    if (byName) return String(byName.id);
  }
  return null;
}

export function syncBulkClassificationFromIngredients(
  row: SuperAdminBulkPreviewRow,
  lookups: SuperAdminBulkLookups
): void {
  const ingredients = row.selections.recipeIngredients || [];
  if (!ingredients.length) return;

  let bestDietRank = 0;
  let bestDietLabel = '';
  for (const ing of ingredients) {
    const label = resolveIngredientDietaryLabel(ing);
    const rank = dietaryRank(label);
    if (rank > bestDietRank) {
      bestDietRank = rank;
      bestDietLabel = label;
    }
  }
  if (bestDietLabel && !row.selections.dietaryPreferenceId) {
    const dietId = findDietaryIdByLabel(lookups, bestDietLabel);
    if (dietId) row.selections.dietaryPreferenceId = dietId;
  }

  let bestHealthRank = 0;
  let bestHealthId: string | null = null;
  for (const ing of ingredients) {
    const id = resolveHealthIdFromIngredient(lookups, ing);
    const rank = healthRank(lookups, id);
    if (rank > bestHealthRank) {
      bestHealthRank = rank;
      bestHealthId = id;
    }
  }
  if (bestHealthId && !row.selections.healthColorCodeId) {
    row.selections.healthColorCodeId = bestHealthId;
  }
}

function needsEnrichment(ing: SuperAdminBulkRecipeIngredient): boolean {
  if (!String(ing.ingredientName || '').trim()) return false;
  const hasMacros =
    (Number(ing.calories) || 0) > 0 ||
    (Number(ing.protein) || 0) > 0 ||
    (Number(ing.fat) || 0) > 0 ||
    (Number(ing.carbs) || 0) > 0 ||
    (Number(ing.fiber) || 0) > 0;
  if (hasMacros && ing.ingredientId > 0) return false;
  return true;
}

function applyFileRecipeMacrosFallback(row: SuperAdminBulkPreviewRow): void {
  const cells = row.cells || {};
  const get = (...keys: string[]) => {
    for (const key of keys) {
      const found = Object.keys(cells).find(
        (k) => k.replace(/[\s_-]+/g, '').toLowerCase() === key.replace(/[\s_-]+/g, '').toLowerCase()
      );
      if (found != null && String(cells[found] || '').trim()) return String(cells[found]).trim();
    }
    return '';
  };

  let calories = Number(get('Calories', 'Energy'));
  let protein = Number(get('Protein'));
  let fat = Number(get('Fat', 'Fats'));
  let carbs = Number(get('Carbs', 'Carbohydrate', 'Carbohydrates'));
  let fiber = Number(get('Fiber', 'Fibre'));

  // e.g. "Energy 1200 Calories | Protein 39.1 g | Carbohydrates 162.7 g | Fiber 25.9 g | Fat 43.6 g"
  const nutritionParams = get('Nutrition Params', 'NutritionParams', 'Nutrition Profiles');
  if (nutritionParams) {
    const readParam = (...labels: string[]): number => {
      for (const label of labels) {
        const re = new RegExp(
          `${label}\\s*[:=]?\\s*(\\d+(?:\\.\\d+)?)`,
          'i'
        );
        const m = nutritionParams.match(re);
        if (m) return Number(m[1]);
      }
      return 0;
    };
    if (!(calories > 0)) calories = readParam('Energy', 'Calories', 'Calorie');
    if (!(protein > 0)) protein = readParam('Protein');
    if (!(fat > 0)) fat = readParam('Fat', 'Fats');
    if (!(carbs > 0)) carbs = readParam('Carbohydrates', 'Carbohydrate', 'Carbs');
    if (!(fiber > 0)) fiber = readParam('Fiber', 'Fibre', 'Dietary Fibre', 'Dietary Fiber');
  }

  const hasAny = [calories, protein, fat, carbs, fiber].some((n) => Number.isFinite(n) && n > 0);
  if (!hasAny) return;

  const ingredients = row.selections.recipeIngredients || [];
  if (!ingredients.length) return;

  const current = ingredients.reduce(
    (acc, i) => ({
      calories: acc.calories + (Number(i.calories) || 0),
      protein: acc.protein + (Number(i.protein) || 0),
      fat: acc.fat + (Number(i.fat) || 0),
      carbs: acc.carbs + (Number(i.carbs) || 0),
      fiber: acc.fiber + (Number(i.fiber) || 0),
      grams: acc.grams + (Number(i.totalGrams) || 0),
    }),
    { calories: 0, protein: 0, fat: 0, carbs: 0, fiber: 0, grams: 0 }
  );

  // Keep ingredient-derived macros only when they look substantial vs file nutrition.
  // Tiny wrong matches (e.g. 9 kcal vs file 1200) must not block the fallback.
  const fileCals = Number.isFinite(calories) && calories > 0 ? calories : 0;
  if (
    current.calories > 0 &&
    fileCals > 0 &&
    current.calories >= Math.max(40, fileCals * 0.2)
  ) {
    return;
  }
  if (
    fileCals <= 0 &&
    (current.calories > 0 || current.protein > 0 || current.fat > 0 || current.carbs > 0)
  ) {
    return;
  }

  const weights = ingredients.map((i) => {
    const g = Number(i.totalGrams) || 0;
    if (g > 0) return g;
    return (
      estimateGramsFromUnit(i.unitLabel || '', Number(i.quantity) || 0, i.ingredientName) ||
      Number(i.quantity) ||
      1
    );
  });
  const weightSum = weights.reduce((a, b) => a + b, 0) || ingredients.length;
  ingredients.forEach((ing, idx) => {
    const share = (weights[idx] || 1) / weightSum;
    ing.calories = (Number.isFinite(calories) ? calories : 0) * share;
    ing.protein = (Number.isFinite(protein) ? protein : 0) * share;
    ing.fat = (Number.isFinite(fat) ? fat : 0) * share;
    ing.carbs = (Number.isFinite(carbs) ? carbs : 0) * share;
    ing.fiber = (Number.isFinite(fiber) ? fiber : 0) * share;
    if (!(Number(ing.totalGrams) > 0)) {
      ing.totalGrams = weights[idx] || 0;
    }
  });
  row.selections.recipeIngredients = [...ingredients];
}

async function resolveCacheEntry(
  name: string,
  deps: BulkIngredientEnricherDeps,
  cache: Map<string, CacheEntry>,
  options: BulkEnrichOptions
): Promise<CacheEntry> {
  const key = normalizeName(name);
  const existing = cache.get(key) || sharedIngredientCache.get(key);

  if (existing) {
    const needUnits =
      !!options.fetchServingUnits && !!existing.hit?.id && !existing.unitsLoaded;
    if (!needUnits) {
      cache.set(key, existing);
      return existing;
    }
  }

  let hit: BulkIngredientSearchHit | null = existing?.hit ?? null;
  let units: BulkServingUnit[] = existing?.units || [];
  let unitsLoaded = !!existing?.unitsLoaded;

  try {
    if (!existing) {
      if (!deps.searchIngredients) {
        hit = null;
      } else {
        const results = await deps.searchIngredients(name);
        hit = pickBestSearchHit(results, name);
      }
    }

    if (options.fetchServingUnits && hit?.id && !unitsLoaded) {
      units = (await deps.getServingUnits(hit.id)) || [];
      unitsLoaded = true;
    }

    const wantDetail =
      !!options.fetchDetail &&
      !!hit?.id &&
      !!deps.getIngredientDetail &&
      !(hit.calories || hit.protein || hit.fat || hit.carbs || hit.fiber);

    if (wantDetail) {
      const detail = await deps.getIngredientDetail!(hit!.id);
      if (detail) {
        hit = {
          ...hit!,
          calories: Number(detail.calories ?? hit!.calories) || 0,
          protein: Number(detail.protein ?? hit!.protein) || 0,
          fat: Number(detail.fat ?? hit!.fat) || 0,
          carbs: Number(detail.carbs ?? hit!.carbs) || 0,
          fiber: Number(detail.fiber ?? hit!.fiber) || 0,
          quantity: Number(detail.quantity ?? hit!.quantity) || hit!.quantity || 100,
          isVeg: detail.isVeg ?? hit!.isVeg ?? null,
          mealPreference: detail.mealPreference || hit!.mealPreference || '',
          healthColorCodeId: detail.healthColorCodeId ?? hit!.healthColorCodeId ?? null,
          healthColorCodeName: detail.healthColorCodeName || hit!.healthColorCodeName || '',
          colorCode: detail.colorCode ?? hit!.colorCode ?? null,
        };
      }
    }
  } catch {
    if (!existing) {
      hit = null;
      units = [];
      unitsLoaded = false;
    }
  }

  const entry: CacheEntry = { hit, units, unitsLoaded };
  cache.set(key, entry);
  sharedIngredientCache.set(key, entry);
  return entry;
}

async function enrichOneIngredient(
  ing: SuperAdminBulkRecipeIngredient,
  deps: BulkIngredientEnricherDeps,
  cache: Map<string, CacheEntry>,
  options: BulkEnrichOptions
): Promise<SuperAdminBulkRecipeIngredient> {
  if (!needsEnrichment(ing) && Number(ing.ingredientId) > 0) return ing;

  const searchName = stripIngredientNoteNoise(ing.ingredientName) || ing.ingredientName;
  const { hit, units } = await resolveCacheEntry(searchName, deps, cache, options);
  if (!hit) {
    const totalGrams = resolveIngredientTotalGrams(
      ing.quantity || 0,
      ing.approximateValue || 0,
      ing.unitLabel || '',
      ing.ingredientName,
      null
    );
    return totalGrams > 0 && !(Number(ing.totalGrams) > 0)
      ? { ...ing, totalGrams }
      : { ...ing, totalGrams: Number(ing.totalGrams) > 0 ? ing.totalGrams : totalGrams };
  }

  let unit = units.length
    ? matchServingUnit(units, ing.unitLabel || '', ing.quantity || 0, ing.approximateValue || 0)
    : null;

  const totalGrams = resolveIngredientTotalGrams(
    ing.quantity || 0,
    ing.approximateValue || 0,
    ing.unitLabel || '',
    ing.ingredientName,
    unit
  );

  const macros = scaleMacros(hit, totalGrams);

  return {
    ...ing,
    ingredientId: hit.id,
    ingredientRefId: Number(hit.ingredientRefId) > 0 ? Number(hit.ingredientRefId) : ing.ingredientRefId || null,
    ingredientName: hit.name || ing.ingredientName,
    servingUnitMasterId: unit?.servingUnitMasterId || ing.servingUnitMasterId || 0,
    unitLabel:
      unit?.displayLabel ||
      ing.unitLabel ||
      (unit
        ? `${unit.size || ''} - ${unit.unit || ''} - ${unit.valueInGram || ''} - ${unit.type || ''}`.trim()
        : ''),
    approximateValue: unit
      ? Number(unit.valueInGram) || ing.approximateValue
      : isGramLikeUnit(ing.unitLabel || '')
        ? ing.approximateValue
        : totalGrams > 0 && Number(ing.quantity) > 0
          ? totalGrams / Number(ing.quantity)
          : ing.approximateValue,
    totalGrams: totalGrams || ing.totalGrams || 0,
    calories: macros.calories,
    protein: macros.protein,
    fat: macros.fat,
    carbs: macros.carbs,
    fiber: macros.fiber,
    isVeg: hit.isVeg ?? ing.isVeg ?? null,
    mealPreference: hit.mealPreference || ing.mealPreference || '',
    healthColorCodeId: hit.healthColorCodeId ?? ing.healthColorCodeId ?? null,
    healthColorCodeName: hit.healthColorCodeName || ing.healthColorCodeName || '',
    colorCode: hit.colorCode ?? ing.colorCode ?? null,
  };
}

function ingredientTotals(row: SuperAdminBulkPreviewRow): {
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
  fiber: number;
  totalGrams: number;
} {
  const list = row.selections.recipeIngredients || [];
  return list.reduce(
    (acc, i) => ({
      calories: acc.calories + (Number(i.calories) || 0),
      protein: acc.protein + (Number(i.protein) || 0),
      fat: acc.fat + (Number(i.fat) || 0),
      carbs: acc.carbs + (Number(i.carbs) || 0),
      fiber: acc.fiber + (Number(i.fiber) || 0),
      totalGrams: acc.totalGrams + (Number(i.totalGrams) || 0),
    }),
    { calories: 0, protein: 0, fat: 0, carbs: 0, fiber: 0, totalGrams: 0 }
  );
}

function normalizeNutritionName(value: any): string {
  return (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ')
    .replace(/\(\s*(kcal|cal|gm|g|mg|mcg|microgram|µg|ug)\s*\)/gi, ' ')
    .replace(/[()]/g, '')
    .replace(/[\/]/g, ' ')
    .replace(/\b(kcal|cal|gm|g|mg|mcg|microgram|µg|ug)\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isCoreMacronutrientRow(nutrition: string): boolean {
  const name = normalizeNutritionName(nutrition);
  if (!name || name.includes('sugar') || name.includes('omega')) return false;
  return (
    name === 'calories' ||
    name === 'calorie' ||
    name === 'energy' ||
    name.includes('calorie') ||
    name.includes('energy') ||
    name.includes('carb') ||
    name === 'protein' ||
    name === 'fat' ||
    name === 'fibre' ||
    name === 'fiber' ||
    name.includes('fibre') ||
    name.includes('fiber')
  );
}

/**
 * Map ingredient detail / nutrition-params API rows → bulk FSSAI nutrient rows.
 * Detail DTO uses Nutrient + Amount; nutrition-params uses nutrition + value.
 */
export function mapBulkIngredientNutrientRows(raw: any[]): BulkIngredientNutrientRow[] {
  if (!Array.isArray(raw) || !raw.length) return [];
  return raw
    .map((p: any) => {
      const nutrition = String(
        p?.nutrition ?? p?.Nutrition ?? p?.nutrient ?? p?.Nutrient ?? p?.fieldName ?? p?.FieldName ?? ''
      ).trim();
      if (!nutrition) return null;
      const rawVal =
        p?.amount ?? p?.Amount ?? p?.value ?? p?.Value ?? p?.nutrientValue ?? p?.NutrientValue;
      if (rawVal === null || rawVal === undefined || rawVal === '') return null;
      const num = Number(rawVal);
      return {
        nutrition,
        value: Number.isFinite(num) ? num : rawVal,
        unit: String(
          p?.unit ?? p?.Unit ?? p?.measurement ?? p?.Measurement ?? p?.uom ?? p?.Uom ?? ''
        ).trim(),
      };
    })
    .filter(Boolean) as BulkIngredientNutrientRow[];
}

function countNonCoreNutrientRows(rows: BulkIngredientNutrientRow[]): number {
  return (rows || []).filter((r) => r && !isCoreMacronutrientRow(String(r.nutrition || ''))).length;
}

export function countNonCoreBulkNutrientRows(rows: BulkIngredientNutrientRow[]): number {
  return countNonCoreNutrientRows(rows);
}

/** Prefer later rows for the same nutrient name (case-insensitive). */
export function mergeBulkNutrientRows(
  primary: BulkIngredientNutrientRow[],
  secondary: BulkIngredientNutrientRow[]
): BulkIngredientNutrientRow[] {
  const byName = new Map<string, BulkIngredientNutrientRow>();
  for (const row of [...(primary || []), ...(secondary || [])]) {
    const key = normalizeNutritionName(row?.nutrition || '');
    if (!key) continue;
    byName.set(key, row);
  }
  return [...byName.values()];
}

/** Same basis as add-recipe: macros + micronutrients → per-100g/ml FSSAI input. */
export async function buildBulkFssaiCalculateInput(
  row: SuperAdminBulkPreviewRow,
  getIngredientNutrientRows?: (ingredientId: number) => Promise<BulkIngredientNutrientRow[]>
): Promise<BulkFssaiCalculateInput | null> {
  const stateOfProduct = String(row.selections.stateOfProduct || '').trim() || 'Solid';
  const totals = ingredientTotals(row);
  const totalGrams = Number(totals.totalGrams) || 0;
  if (totalGrams <= 0) return null;

  const nutrients = new Map<string, { absolute: number; unit: string }>();
  const addAbsolute = (name: string, absolute: number, unit: string, allowZero = false): void => {
    const label = String(name || '').trim();
    if (!label || !Number.isFinite(absolute)) return;
    if (!allowZero && absolute === 0 && !isCoreMacronutrientRow(label)) return;
    const existing = nutrients.get(label);
    if (existing) {
      existing.absolute += absolute;
      if (!existing.unit && unit) existing.unit = unit;
    } else {
      nutrients.set(label, { absolute, unit: String(unit || '').trim() });
    }
  };

  addAbsolute('Calories', Number(totals.calories) || 0, 'kcal', true);
  addAbsolute('Carbohydrate', Number(totals.carbs) || 0, 'g', true);
  addAbsolute('Protein', Number(totals.protein) || 0, 'g', true);
  addAbsolute('Fat', Number(totals.fat) || 0, 'g', true);
  addAbsolute('Fibre', Number(totals.fiber) || 0, 'g', true);

  if (getIngredientNutrientRows) {
    const nutrientCache = new Map<number, BulkIngredientNutrientRow[]>();
    await Promise.all(
      (row.selections.recipeIngredients || []).map(async (ing) => {
        const ingGrams = Number(ing?.totalGrams) || 0;
        const masterId = Number(ing?.ingredientId) || 0;
        if (ingGrams <= 0 || masterId <= 0) return;

        let rows = nutrientCache.get(masterId);
        if (!rows) {
          try {
            rows = (await getIngredientNutrientRows(masterId)) || [];
          } catch {
            rows = [];
          }
          nutrientCache.set(masterId, rows);
        }

        for (const r of rows) {
          const name = String(r?.nutrition || '').trim();
          if (!name || isCoreMacronutrientRow(name)) continue;
          const per100 = Number(r.value);
          if (!Number.isFinite(per100) || per100 === 0) continue;
          addAbsolute(name, (per100 * ingGrams) / 100, String(r.unit || '').trim());
        }
      })
    );
  }

  const scaleToPer100 = 100 / totalGrams;
  const toPer100 = (absolute: number) => absolute * scaleToPer100;

  const readMacro = (...names: string[]): number | undefined => {
    for (const [label, entry] of nutrients.entries()) {
      const normalized = normalizeNutritionName(label);
      if (
        names.some(
          (name) =>
            normalized === normalizeNutritionName(name) ||
            normalized.includes(normalizeNutritionName(name))
        )
      ) {
        return toPer100(entry.absolute);
      }
    }
    return undefined;
  };

  const nutrientValues: Record<string, number> = {};
  const nutrientUnits: Record<string, string> = {};
  nutrients.forEach((entry, name) => {
    if (isCoreMacronutrientRow(name)) return;
    nutrientValues[name] = toPer100(entry.absolute);
    if (entry.unit) nutrientUnits[name] = entry.unit;
  });

  return {
    stateOfProduct,
    calories: readMacro('Calories', 'Energy'),
    protein: readMacro('Protein'),
    fat: readMacro('Fat', 'Total Fat'),
    fiber: readMacro('Fibre', 'Fiber', 'Dietary Fibre', 'Dietary Fiber'),
    carbs: readMacro('Carbohydrate', 'Carbohydrates', 'Carbs'),
    nutrientValues,
    nutrientUnits,
  };
}

/**
 * Match file ingredient names to master and fill macros / Health Color / Dietary Preference.
 * Use `options.fast` on upload (search-only). FSSAI runs later in the row editor.
 */
export async function enrichSuperAdminBulkRowsFromIngredients(
  rows: SuperAdminBulkPreviewRow[],
  lookups: SuperAdminBulkLookups,
  deps: BulkIngredientEnricherDeps,
  onProgress?: (done: number, total: number) => void,
  options: BulkEnrichOptions = {}
): Promise<void> {
  const fast = options.fast !== false; // default fast
  const opts: BulkEnrichOptions = {
    fast,
    concurrency: options.concurrency ?? (fast ? 12 : 6),
    calculateFssai: options.calculateFssai ?? !fast,
    fetchServingUnits: options.fetchServingUnits ?? !fast,
    fetchDetail: options.fetchDetail ?? false,
  };

  const cache = new Map<string, CacheEntry>();
  const names = new Set<string>();
  (rows || []).forEach((row) => {
    (row.selections.recipeIngredients || []).forEach((ing) => {
      if (needsEnrichment(ing) || !(Number(ing.ingredientId) > 0)) {
        const key = normalizeName(ing.ingredientName);
        if (key) names.add(key);
      }
    });
  });

  // Preserve original casing for search from first matching ingredient name.
  const nameByKey = new Map<string, string>();
  (rows || []).forEach((row) => {
    (row.selections.recipeIngredients || []).forEach((ing) => {
      const key = normalizeName(ing.ingredientName);
      if (key && names.has(key) && !nameByKey.has(key)) {
        nameByKey.set(key, ing.ingredientName.trim());
      }
    });
  });

  const uniqueKeys = [...names];
  let done = 0;

  // Fast professional path: one bulk resolve for all unique names.
  if (deps.bulkResolveIngredients && uniqueKeys.length) {
    onProgress?.(0, uniqueKeys.length);
    try {
      const displayNames = uniqueKeys.map((key) => nameByKey.get(key) || key);
      const resolved = await deps.bulkResolveIngredients(displayNames);
      for (const key of uniqueKeys) {
        const display = nameByKey.get(key) || key;
        const hit =
          resolved.get(normalizeName(display)) ??
          resolved.get(key) ??
          resolved.get(display) ??
          null;
        const entry: CacheEntry = { hit, units: [], unitsLoaded: true };
        cache.set(key, entry);
        sharedIngredientCache.set(key, entry);
      }
      onProgress?.(uniqueKeys.length, uniqueKeys.length);
    } catch (err) {
      console.warn('Bulk ingredient resolve failed; falling back to per-name search', err);
      done = 0;
      if (deps.searchIngredients) {
        const concurrency = Math.max(1, opts.concurrency || 8);
        for (let i = 0; i < uniqueKeys.length; i += concurrency) {
          const chunk = uniqueKeys.slice(i, i + concurrency);
          await Promise.all(
            chunk.map(async (key) => {
              await resolveCacheEntry(nameByKey.get(key) || key, deps, cache, opts);
              done += 1;
              onProgress?.(done, uniqueKeys.length);
            })
          );
        }
      }
    }
  } else {
    const concurrency = Math.max(1, opts.concurrency || 8);
    for (let i = 0; i < uniqueKeys.length; i += concurrency) {
      const chunk = uniqueKeys.slice(i, i + concurrency);
      await Promise.all(
        chunk.map(async (key) => {
          await resolveCacheEntry(nameByKey.get(key) || key, deps, cache, opts);
          done += 1;
          onProgress?.(done, uniqueKeys.length);
        })
      );
    }
  }

  for (const row of rows || []) {
    const next: SuperAdminBulkRecipeIngredient[] = [];
    for (const ing of row.selections.recipeIngredients || []) {
      next.push(await enrichOneIngredient(ing, deps, cache, opts));
    }
    row.selections.recipeIngredients = next;
    row.selections.ingredients = next.map((ing) => ({
      name: ing.ingredientName,
      quantity: ing.quantity,
      approximateGrams: ing.totalGrams || ing.approximateValue || 0,
      unitLabel: ing.unitLabel || undefined,
    }));

    applyFileRecipeMacrosFallback(row);
    syncBulkClassificationFromIngredients(row, lookups);

    if (!row.selections.stateOfProduct) {
      row.selections.stateOfProduct = 'Solid';
    }

    if (opts.calculateFssai && deps.calculateFssaiTags && row.selections.stateOfProduct) {
      try {
        const input = await buildBulkFssaiCalculateInput(
          row,
          opts.fast ? undefined : deps.getIngredientNutrientRows
        );
        if (input) {
          row.selections.generatedFssaiNutrientTags = await deps.calculateFssaiTags(input);
        }
      } catch {
        // keep existing / empty
      }
    }
  }
}
